/**
 * Unified CAD-source aggregator for the Control Surfaces workbench.
 *
 * Three DURABLE, project-scoped sources have authed, typed list endpoints:
 *   - backend example geometries  (listExamples)
 *   - project aircraft designs     (listAircraftDesigns, export-on-pick)
 *   - nTop parametric notebooks    (listNtopStatus - partner .ntop files with
 *                                   declared inputs + optional pre-baked frames)
 * plus the EPHEMERAL "this session" entries the tab already holds (a seed handed
 * from the Aircraft screen, a manually-typed step path / assembly id). Live
 * upload assemblies cannot be truthfully served per project (no project_id on
 * the record, per-process memory) so they are never listed here - only the
 * source the current tab is actually pointed at.
 *
 * Aggregation is client-side with Promise.allSettled so a failure in ONE group
 * degrades only that group (the designs endpoint 503s when the VSP engine is
 * absent - that must not hide the examples). The ntop routes may simply NOT
 * EXIST in an environment (older API): a 404 there means "not available" - the
 * group renders nothing, with no error chip - while any other failure surfaces
 * as that group's error like the siblings.
 */

import { listExamples, type ExampleGeometry } from "../../api/controlSurface";
import { listAircraftDesigns, type AircraftDesignSummary } from "../../api/aircraft";
import { listNtopStatus, type NtopNotebook, type NtopStatusResponse } from "../../api/ntop";

/** A pickable CAD source, tagged by where it came from. */
export type CsSource =
  | {
      kind: "example";
      key: string;
      label: string;
      stepPath: string;
      fileKind: ExampleGeometry["kind"];
      sizeMb: number;
      group: ExampleGeometry["group"];
    }
  | {
      kind: "design";
      designId: string;
      label: string;
      base: string;
      opsCount: number;
      updatedAt?: string;
    }
  | {
      kind: "session";
      label: string;
      stepPath?: string;
      assemblyId?: string;
      origin: "seed" | "bake" | "manual";
    }
  | {
      kind: "ntop";
      key: string;
      label: string;
      sizeMb: number;
      /** Baked frame count, or null when the notebook has no frame set at all. */
      frameCount: number | null;
      /** True when a NON-EMPTY frame set exists (the near-instant scrub path). */
      hasFrames: boolean;
    };

export interface CsSourcesResult {
  examples: ExampleGeometry[];
  examplesError: string | null;
  designs: AircraftDesignSummary[];
  designsError: string | null;
  /** The full ntop status (engine + notebooks), or null when unavailable. */
  ntop: NtopStatusResponse | null;
  /** Non-404 ntop failure, for the group's error note. A 404 (routes absent in
   *  this environment) is NOT an error - ntop is simply null. */
  ntopError: string | null;
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * Fetch all three durable source groups at once. Each group degrades
 * independently: a rejected examples fetch yields [] + its error while designs
 * and ntop still load, and so on for every permutation.
 */
export async function loadCsSources(projectId: string): Promise<CsSourcesResult> {
  const [ex, dz, nt] = await Promise.allSettled([
    listExamples(projectId),
    listAircraftDesigns(projectId),
    listNtopStatus(projectId),
  ]);
  // apiClient throws "API 404: ..." when the ntop routes do not exist (an
  // environment without the feature) - honest "not available", not an error.
  const ntopFailure = nt.status === "rejected" ? errorMessage(nt.reason) : null;
  return {
    examples: ex.status === "fulfilled" ? ex.value.examples ?? [] : [],
    examplesError: ex.status === "rejected" ? errorMessage(ex.reason) : null,
    designs: dz.status === "fulfilled" ? dz.value.designs ?? [] : [],
    designsError: dz.status === "rejected" ? errorMessage(dz.reason) : null,
    ntop: nt.status === "fulfilled" ? nt.value ?? null : null,
    ntopError: ntopFailure && !/\bAPI 404\b/.test(ntopFailure) ? ntopFailure : null,
  };
}

/** Map a backend example into the unified source shape. */
export function exampleToSource(ex: ExampleGeometry): Extract<CsSource, { kind: "example" }> {
  return {
    kind: "example",
    key: ex.key,
    label: ex.label,
    stepPath: ex.path,
    fileKind: ex.kind,
    sizeMb: ex.size_mb,
    group: ex.group,
  };
}

/** Map a project design into the unified source shape (export happens on pick). */
export function designToSource(d: AircraftDesignSummary): Extract<CsSource, { kind: "design" }> {
  return {
    kind: "design",
    designId: d.design_id,
    label: d.name,
    base: d.base,
    opsCount: d.ops_count,
    updatedAt: d.updated_at,
  };
}

/** Map an nTop notebook into the unified source shape (frames drive the meta). */
export function ntopToSource(nb: NtopNotebook): Extract<CsSource, { kind: "ntop" }> {
  const frameCount = nb.frames ? nb.frames.frames.length : null;
  return {
    kind: "ntop",
    key: nb.key,
    label: nb.label,
    sizeMb: nb.size_mb,
    frameCount,
    hasFrames: frameCount !== null && frameCount > 0,
  };
}
